/*
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 UNESP Universidade Estadual Paulista "Júlio de Mesquita Filho"
 * Copyright 2024-2026 UNICAMP Universidade Estadual de Campinas
 *
 */

import { Component, EventEmitter, HostListener, OnInit, Output, DestroyRef, inject } from '@angular/core'
import { FormControl, FormGroup, Validators } from '@angular/forms'
import { InscricaoSituacaoService } from 'src/app/services/inscricao-situacao.service'
import { InscricaoService } from 'src/app/services/inscricao.service'
import { UnespCoreMessageService } from 'src/libs/unesp-core'
import { ActivatedRoute } from '@angular/router'
import { InscricaoSituacao } from 'src/app/models/inscricao-situacao'
import { InscricaoFicha } from 'src/app/models/inscricao-ficha'
import { SituacaoInscricao } from 'src/app/enums/situacao-inscricao'
import { TipoConcurso } from 'src/app/enums/tipo-concurso'
import { PermitirAlteracaoService } from 'src/app/services/permitir-alteracao.service'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'

@Component({
  selector: 'app-inscricao-situacao',
  templateUrl: './inscricao-situacao.component.html',
  styleUrls: ['./inscricao-situacao.component.scss'],
})
export class InscricaoSituacaoComponent implements OnInit {
  private destroyRef = inject(DestroyRef)

  @Output() cancelar = new EventEmitter<void>()
  isPermitirAlteracao: boolean
  isAtualizado: boolean
  id?: number
  inscricaoSituacao?: InscricaoSituacao
  inscricao?: InscricaoFicha
  situacaoInscricao = SituacaoInscricao

  get situacoesDisponiveis() {
    // Para concursos PTPP, não exibir DEFERIDA para seleção manual
    // mas se já estiver DEFERIDA, manter o valor
    const tipoConcursoStr = String(this.inscricao?.tipoConcurso || '')
    const isPTPP = this.inscricao?.tipoConcurso === TipoConcurso.PTPP || 
                   tipoConcursoStr === 'PTPP' ||
                   tipoConcursoStr === 'Professor Doutor - Pretos e Pardos'
    
    if (isPTPP) {
      const situacoes: any = {        
        NAO_CONFIRMADA: SituacaoInscricao.NAO_CONFIRMADA,
        INDEFERIDA: SituacaoInscricao.INDEFERIDA,
        HETEROIDENTIFICACAO: SituacaoInscricao.HETEROIDENTIFICACAO
      }
      // Se o candidato já está DEFERIDA, incluir na lista
      if (this.inscricaoSituacao?.status === 'DEFERIDA') {
        situacoes.DEFERIDA = SituacaoInscricao.DEFERIDA
      }
      return situacoes
    }
    
    // Para outros tipos de concurso, remover HETEROIDENTIFICACAO
    const { HETEROIDENTIFICACAO, ...situacoesSemHetero } = SituacaoInscricao
    
    // Se não concorre a vaga de deficiente, remover também DEFERIDA_SEM_PCD
    if (this.inscricao?.concorrerVagaDeficiente) {
      return situacoesSemHetero
    } else {
      const { DEFERIDA_SEM_PCD, ...situacoesSemPCD } = situacoesSemHetero
      return situacoesSemPCD
    }
  }

  form = new FormGroup({
    status: new FormControl('', Validators.required),
    statusMotivo: new FormControl(''),
    documentoJuradoInvalido: new FormControl<string | null>(null),
  })

  constructor(
    private inscricaoSituacaoService: InscricaoSituacaoService,
    private inscricaoService: InscricaoService,
    private permitirAlteracaoService: PermitirAlteracaoService,
    private unespCoreMessageService: UnespCoreMessageService,
    private route: ActivatedRoute
  ) {
    this.isPermitirAlteracao = this.route.snapshot.url[0].toString() == 'permitir-alteracao'
    this.isAtualizado = false
  }

  ngOnInit(): void {
    this.route.params.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      this.id = params['id']
      this.form.reset()
      if (this.id) {
        this.inscricaoService.get(this.id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(inscricao => {
          this.inscricao = inscricao
          if (inscricao.jurado) {
            this.form.get('documentoJuradoInvalido')?.setValidators(Validators.required)
            this.form.get('documentoJuradoInvalido')?.updateValueAndValidity()
          }
        })
        
        this.inscricaoSituacaoService.get(this.id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(obj => {
          this.inscricaoSituacao = obj
          this.form.get('status')?.setValue(obj.status)
          this.form.get('statusMotivo')?.setValue(obj.statusMotivo)
          this.form.get('documentoJuradoInvalido')?.setValue(
            obj.documentoJuradoInvalido != null ? obj.documentoJuradoInvalido.toString() : null
          )
          this.isAtualizado = true

          this.form
            .get('status')
            ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
              value =>
                (this.isAtualizado =
                  this.inscricaoSituacao?.status == value &&
                  this.inscricaoSituacao?.statusMotivo == this.form.get('statusMotivo')?.value)
            )

          this.form
            .get('statusMotivo')
            ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
              value =>
                (this.isAtualizado =
                  this.inscricaoSituacao?.statusMotivo == value &&
                  this.inscricaoSituacao?.status == this.form.get('status')?.value)
            )

          if (this.inscricaoSituacao.statusConcluida || this.isPermitirAlteracao) {
            this.form.disable()
          }

          if (!this.inscricaoSituacao.concursoAposPeriodoInscricao) {
            this.form.get('status')?.disable()
            this.form.get('statusMotivo')?.disable()
            this.form.get('documentoJuradoInvalido')?.disable()
          } 
        })
      }
    })
  }

  gravar(): void {
      if (this.id && !this.inscricaoSituacao?.statusConcluida) {
        if (!this.form.valid) {
          this.form.markAllAsTouched()
          this.unespCoreMessageService.showMessageError('Preencha todos os campos obrigatórios')
          return
        }
        let formulario = this.form.value as Partial<InscricaoSituacao>
        if ((formulario.status == 'INDEFERIDA' || formulario.status == 'DEFERIDA_SEM_PCD') && (formulario.statusMotivo == null || formulario.statusMotivo.trim() == '')) {
          this.unespCoreMessageService.showMessageError(`É preciso informar o Motivo`)
        } else {
          this.inscricaoSituacaoService.alterar(this.id, formulario as InscricaoSituacao).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(inscricaoSituacao => {
            this.inscricaoSituacao = inscricaoSituacao
            this.inscricaoService.get(this.id!).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(inscricao => {
              this.inscricao = inscricao
            })
            this.unespCoreMessageService.showMessageSuccess(`Situação #${this.id} atualizada com sucesso`)
            this.isAtualizado =
              this.inscricaoSituacao?.status == this.form.get('status')?.value &&
              this.inscricaoSituacao?.statusMotivo == this.form.get('statusMotivo')?.value
          })
        }
      }
  }

  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent): void {
    if (this.hasPendingChanges()) {
      event.preventDefault()
    }
  }

  hasPendingChanges(): boolean {
    return !this.isAtualizado && this.inscricaoSituacao != null && !this.inscricaoSituacao.statusConcluida && this.form.dirty
  }
  permitirAlteracao() {
    if (this.id && this.isPermitirAlteracao && this.inscricaoSituacao) {
      this.permitirAlteracaoService.liberarRegistro('deferimento', this.id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(registro => {
        this.inscricaoSituacao!.statusConcluida = registro.status.concluido
        this.unespCoreMessageService.showMessageSuccess(`Situação #${this.id} revertido para não concluído`)
      })
    }
  }
}
